import { useEffect, useRef, useState } from "react";
import {
  HOME_TYPES, STATUSES, RECENCY, FEATURES, NEW_TO_MARKET, GARAGE_COUNTS,
} from "@suresend/shared/connect/searchFilters";
import { locationLabel, rememberLocationLabels } from "@suresend/shared/connect/locationLabels";
import { MIN_CHARS, useLocationSuggestions } from "./useLocationSuggestions";

// SUR-1868 — advanced filter panel for the Custom Search island, at
// ezhomesearch.com parity. A focused modal of toggle-pills + ranges that emits
// the canonical ez `query` object (matches Connect::SavedSearchParams). Plain
// styled controls (.ssc-* classes in the connect layout) — the Connect bundle is
// a separate, lean esbuild entry and must NOT import the SPA's components/ui.
//
// The filter VOCABULARY (which filters exist + their labels) lives in
// @suresend/shared/connect/searchFilters so the CRM saved-search editor offers
// exactly the same set.

const CURRENT_YEAR = new Date().getFullYear();

const ALL_BOOLEANS = [...HOME_TYPES, ...STATUSES, ...RECENCY];
const BOOLEAN_LABELS = Object.fromEntries(ALL_BOOLEANS);
const FEATURE_LABELS = Object.fromEntries(FEATURES);
const money = (n) => `$${Number(n).toLocaleString("en-US")}`;

// The primary search scope belongs to the toolbar, so the modal must preserve it
// (notably on "Clear all"). Secondary locations are edited by this modal.
export const LOCATION_KEYS = ["search"];

// Count active (non-default) filter keys for the trigger badge. The primary
// searched location is excluded because it is owned by the toolbar.
// Secondary locations are refinements owned by this modal and count as one filter.
export function activeFilterCount(query) {
  return Object.keys(query || {}).filter((k) => {
    if (LOCATION_KEYS.includes(k)) return false;
    const v = query[k];
    if (Array.isArray(v)) return v.length > 0;
    return v !== undefined && v !== "" && v !== false && v !== 0;
  }).length;
}

// Human, REMOVABLE active-filter chips for the toolbar. Each chip's `remove(q)`
// returns a new query with that filter cleared.
export function describeQuery(query = {}) {
  const chips = [];
  const omit = (...keys) => (q) => { const n = { ...q }; keys.forEach((k) => delete n[k]); return n; };

  (query.search || []).forEach((loc, i) => {
    chips.push({
      id: `loc:${i}`, label: locationLabel(loc),
      remove: (q) => { const s = (q.search || []).filter((_, j) => j !== i); const n = { ...q }; if (s.length) n.search = s; else delete n.search; return n; },
    });
  });
  // Secondary locations (cities / neighborhoods / schools); a "-"-prefix is an
  // ez exclusion, so label it "Exclude …".
  (query.secondary_locations || []).forEach((loc, i) => {
    const excluded = loc.startsWith("-");
    const name = locationLabel(loc);
    chips.push({
      id: `sec:${i}`, label: excluded ? `Exclude ${name}` : name,
      remove: (q) => { const s = (q.secondary_locations || []).filter((_, j) => j !== i); const n = { ...q }; if (s.length) n.secondary_locations = s; else delete n.secondary_locations; return n; },
    });
  });
  if (query.min_price || query.max_price) {
    const label = query.min_price && query.max_price ? `${money(query.min_price)}–${money(query.max_price)}`
      : query.min_price ? `${money(query.min_price)}+` : `Up to ${money(query.max_price)}`;
    chips.push({ id: "price", label, remove: omit("min_price", "max_price") });
  }
  [["min_beds", "max_beds", "bd"], ["min_baths", "max_baths", "ba"]].forEach(([lo, hi, unit]) => {
    if (query[lo] || query[hi]) {
      const label = query[lo] && query[hi] ? `${query[lo]}–${query[hi]} ${unit}`
        : query[lo] ? `${query[lo]}+ ${unit}` : `≤${query[hi]} ${unit}`;
      chips.push({ id: lo, label, remove: omit(lo, hi) });
    }
  });
  [["min_square_footage", "max_square_footage", "sq ft"], ["min_lot_size", "max_lot_size", "ac lot"],
   ["min_year_built", "max_year_built", "built"]].forEach(([lo, hi, unit]) => {
    if (query[lo] || query[hi]) {
      const label = query[lo] && query[hi] ? `${query[lo]}–${query[hi]} ${unit}` : query[lo] ? `${query[lo]}+ ${unit}` : `≤${query[hi]} ${unit}`;
      chips.push({ id: lo, label, remove: omit(lo, hi) });
    }
  });
  // HOA / tax are money ranges — format with $ rather than the bare-number unit loop above.
  [["min_hoa", "max_hoa", "HOA"], ["min_tax", "max_tax", "tax"]].forEach(([lo, hi, unit]) => {
    if (query[lo] || query[hi]) {
      const label = query[lo] && query[hi] ? `${money(query[lo])}–${money(query[hi])} ${unit}`
        : query[lo] ? `${money(query[lo])}+ ${unit}` : `≤${money(query[hi])} ${unit}`;
      chips.push({ id: lo, label, remove: omit(lo, hi) });
    }
  });
  if (query.garage_spaces) chips.push({ id: "garage_spaces", label: `${query.garage_spaces}+ garage`, remove: omit("garage_spaces") });
  if (query.new_to_market) chips.push({ id: "new_to_market", label: `Last ${query.new_to_market} days`, remove: omit("new_to_market") });
  Object.keys(BOOLEAN_LABELS).forEach((k) => { if (query[k]) chips.push({ id: k, label: BOOLEAN_LABELS[k], remove: omit(k) }); });
  Object.keys(FEATURE_LABELS).forEach((k) => {
    if (query[k]) chips.push({ id: k, label: query[k] === -1 ? `No ${FEATURE_LABELS[k].toLowerCase()}` : FEATURE_LABELS[k], remove: omit(k) });
  });
  if (query.keywords) chips.push({ id: "keywords", label: `“${query.keywords}”`, remove: omit("keywords") });
  return chips;
}

export function normalizeCountRanges(query = {}) {
  const next = { ...query };
  [["min_beds", "max_beds"], ["min_baths", "max_baths"]].forEach(([lo, hi]) => {
    const min = Number(next[lo]);
    const max = Number(next[hi]);
    if (!(min > 0)) delete next[lo];
    else next[lo] = Math.floor(min);
    if (!(max > 0)) delete next[hi];
    else next[hi] = Math.floor(max);
    if (next[lo] && next[hi] && next[lo] > next[hi]) [next[lo], next[hi]] = [next[hi], next[lo]];
  });
  return next;
}

const sqftFmt = (v) => v.toLocaleString("en-US");

// NOTE: these are MODULE-LEVEL so their component identity is stable across the
// panel's re-renders. Defining them inside SearchFilterPanel made React unmount/
// remount the <input> on every setDraft — which broke slider dragging (the drag
// lost its DOM element mid-gesture).

function Pill({ active, onClick, children }) {
  return <button type="button" className="ssc-pill-btn" aria-pressed={active} onClick={onClick}>{children}</button>;
}

// Segmented min-only control (garage).
function Segmented({ options, ariaLabel, value, onSelect }) {
  return (
    <div className="ssc-seg" role="group" aria-label={ariaLabel}>
      {options.map(([v, label]) => (
        <button key={`${ariaLabel}-${v}`} type="button" aria-pressed={(value ?? "") === v} onClick={() => onSelect(v)}>{label}</button>
      ))}
    </div>
  );
}

// Type-in count range for beds / baths. Empty means unbounded. The server is
// authoritative, but min/step also keep normal browser interactions sensible.
function CountRange({ label, loKey, hiKey, draft, set }) {
  return (
    <div>
      <span className="ssc-group-title">{label}</span>
      <div className="ssc-quick-range">
        <input type="number" min="0" step="1" inputMode="numeric" className="ssc-field"
               aria-label={`Minimum ${label.toLowerCase()}`} placeholder="No min"
               value={draft[loKey] ?? ""}
               onChange={(e) => set(loKey, e.target.value === "" ? "" : Number(e.target.value))} />
        <span className="ssc-quick-dash">–</span>
        <input type="number" min="0" step="1" inputMode="numeric" className="ssc-field"
               aria-label={`Maximum ${label.toLowerCase()}`} placeholder="No max"
               value={draft[hiKey] ?? ""}
               onChange={(e) => set(hiKey, e.target.value === "" ? "" : Number(e.target.value))} />
      </div>
    </div>
  );
}

// Bounded type-ahead for refinements inside the chosen primary search area.
// ez returns only compatible cities, neighborhoods, and schools when `within`
// is present. Picks stay in the modal draft until the visitor applies filters.
function SecondaryLocationPicker({ locations, value, onChange }) {
  const [text, setText] = useState("");
  const [focused, setFocused] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const boxRef = useRef(null);
  const listRef = useRef(null);
  const disabled = locations.length === 0;
  const { suggestions, loading, clear } = useLocationSuggestions({
    text: disabled ? "" : text,
    within: locations,
  });
  const menuOpen = focused && !disabled && text.trim().length >= MIN_CHARS;

  useEffect(() => { setActiveIndex(0); }, [suggestions]);
  useEffect(() => {
    if (!disabled) return;
    setText("");
    setFocused(false);
    clear();
  }, [disabled]);
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setFocused(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [menuOpen]);
  useEffect(() => {
    if (menuOpen) listRef.current?.children?.[activeIndex]?.scrollIntoView?.({ block: "nearest" });
  }, [activeIndex, menuOpen]);

  const pick = (suggestion) => {
    // Keep the text: a gazetteer id ("City:<uuid>") has no name in it.
    rememberLocationLabels([suggestion]);
    if (suggestion?.id && !value.includes(suggestion.id)) onChange([...value, suggestion.id]);
    setText("");
    setFocused(false);
    clear();
  };
  const onKeyDown = (e) => {
    if (!menuOpen) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && suggestions[activeIndex]) {
      e.preventDefault();
      pick(suggestions[activeIndex]);
    } else if (e.key === "Escape") {
      setFocused(false);
    }
  };

  return (
    <div className="ssc-secondary-locations" ref={boxRef}>
      <div className="ssc-secondary-input">
        {value.map((id) => (
          <span key={id} className="ssc-loc-chip">
            {locationLabel(id)}
            <button type="button" aria-label={`Remove ${locationLabel(id)}`} onClick={() => onChange(value.filter((item) => item !== id))}>
              <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" /></svg>
            </button>
          </span>
        ))}
        <input
          type="text"
          value={text}
          disabled={disabled}
          aria-label="Cities, neighborhoods, or schools"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={menuOpen}
          placeholder={disabled ? "Add a location above first" : "Search cities, neighborhoods, or schools"}
          onFocus={() => setFocused(true)}
          onChange={(e) => { setText(e.target.value); setFocused(true); }}
          onKeyDown={onKeyDown}
        />
      </div>
      {menuOpen && (
        <ul className="ssc-loc-menu" role="listbox" ref={listRef}>
          {suggestions.map((suggestion, index) => (
            <li key={suggestion.id} role="option" aria-selected={index === activeIndex}>
              <button type="button" className={`ssc-loc-opt${index === activeIndex ? " is-active" : ""}`}
                      onMouseDown={(e) => e.preventDefault()} onMouseEnter={() => setActiveIndex(index)} onClick={() => pick(suggestion)}>
                <span className="ssc-loc-opt-text">{suggestion.text}</span>
                {suggestion.category && <span className="ssc-loc-cat">{suggestion.category}</span>}
              </button>
            </li>
          ))}
          {suggestions.length === 0 && <li className="ssc-loc-note" role="option" aria-disabled="true">{loading ? "Searching…" : "No matching locations"}</li>}
        </ul>
      )}
      <span className="ssc-field-help">
        {disabled ? "Choose a primary location above the filters first." : "Refine your search with one or more cities, neighborhoods, or schools."}
      </span>
    </div>
  );
}

// Dual-handle min/max slider. A value at the domain floor/ceiling means "unset"
// (omitted from the query). Thumbs can't cross (clamped by one step).
function RangeSlider({ loKey, hiKey, min, max, step, fmt, draft, set }) {
  const lo = draft[loKey] ?? min;
  const hi = draft[hiKey] ?? max;
  const pct = (v) => ((v - min) / (max - min)) * 100;
  const onLo = (e) => { const v = Math.min(Number(e.target.value), hi - step); set(loKey, v <= min ? "" : v); };
  const onHi = (e) => { const v = Math.max(Number(e.target.value), lo + step); set(hiKey, v >= max ? "" : v); };
  return (
    <>
      <div className="ssc-slider-readout">
        <span>{lo <= min ? "No min" : fmt(lo)}</span>
        <span>{hi >= max ? "No max" : fmt(hi)}</span>
      </div>
      <div className="ssc-slider">
        <div className="ssc-slider-rail" />
        <div className="ssc-slider-fill" style={{ left: `${pct(lo)}%`, right: `${100 - pct(hi)}%` }} />
        <input type="range" className="ssc-slider-input" aria-label={loKey} min={min} max={max} step={step} value={lo} onChange={onLo} />
        <input type="range" className="ssc-slider-input" aria-label={hiKey} min={min} max={max} step={step} value={hi} onChange={onHi} />
      </div>
    </>
  );
}

export default function SearchFilterPanel({ value = {}, onApply }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value);

  // Sync the draft from the live query whenever the modal (re)opens.
  useEffect(() => { if (open) setDraft(value); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close on Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const set = (key, val) => setDraft((d) => {
    const next = { ...d };
    if (val === "" || val === undefined || val === null || val === false || val === 0 || val === "0") delete next[key];
    else next[key] = val;
    return next;
  });
  const num = (key) => (e) => set(key, e.target.value === "" ? "" : Number(e.target.value));
  const toggleBool = (key) => () => set(key, !draft[key]);
  const toggleFeature = (key) => () => set(key, draft[key] === 1 ? 0 : 1);

  const apply = () => { onApply?.(normalizeCountRanges(draft)); setOpen(false); };
  // "Clear all" clears modal-owned filters (including secondary refinements),
  // but the primary searched location must survive or the result scope would
  // silently broaden to the tenant-wide feed.
  const clearAll = () => setDraft((d) => {
    const kept = {};
    LOCATION_KEYS.forEach((k) => { if (d[k] !== undefined) kept[k] = d[k]; });
    return kept;
  });
  const count = activeFilterCount(value);

  return (
    <>
      <button type="button" className="ssc-btn" aria-expanded={open} onClick={() => setOpen(true)}>
        <svg className="ssc-ico" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <path d="M4 6h10M4 12h7M4 18h4M16 5v6m0 0l-2.5-2.5M16 11l2.5-2.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        All Filters{count > 0 && <span className="ssc-count">{count}</span>}
      </button>

      {open && (
        <div className="ssc-modal" role="dialog" aria-modal="true" aria-label="Search filters"
             onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}>
          <div className="ssc-modal-card">
            <div className="ssc-modal-head">
              <h3>Filters</h3>
              <button type="button" className="ssc-modal-close" aria-label="Close filters" onClick={() => setOpen(false)}>&times;</button>
            </div>

            <div className="ssc-modal-body">
              <div className="ssc-group">
                <span className="ssc-group-title">Include cities, neighborhoods, and schools</span>
                <SecondaryLocationPicker
                  locations={Array.isArray(draft.search) ? draft.search : []}
                  value={Array.isArray(draft.secondary_locations) ? draft.secondary_locations : []}
                  onChange={(locations) => set("secondary_locations", locations.length ? locations : "")}
                />
              </div>

              <div className="ssc-group">
                <span className="ssc-group-title">Price</span>
                <RangeSlider loKey="min_price" hiKey="max_price" min={0} max={3000000} step={25000} fmt={money} draft={draft} set={set} />
              </div>

              <div className="ssc-group ssc-grid2">
                <CountRange label="Beds" loKey="min_beds" hiKey="max_beds" draft={draft} set={set} />
                <CountRange label="Baths" loKey="min_baths" hiKey="max_baths" draft={draft} set={set} />
              </div>

              <div className="ssc-group">
                <span className="ssc-group-title">Home type</span>
                <div className="ssc-pills">
                  {HOME_TYPES.map(([key, label]) => (
                    <Pill key={key} active={!!draft[key]} onClick={toggleBool(key)}>{label}</Pill>
                  ))}
                </div>
              </div>

              <div className="ssc-group">
                <span className="ssc-group-title">Square feet</span>
                <RangeSlider loKey="min_square_footage" hiKey="max_square_footage" min={0} max={10000} step={100} fmt={sqftFmt} draft={draft} set={set} />
              </div>
              <div className="ssc-group">
                <span className="ssc-group-title">Lot size (acres)</span>
                <RangeSlider loKey="min_lot_size" hiKey="max_lot_size" min={0} max={25} step={0.25} fmt={(v) => `${v} ac`} draft={draft} set={set} />
              </div>
              <div className="ssc-group">
                <span className="ssc-group-title">Year built</span>
                <RangeSlider loKey="min_year_built" hiKey="max_year_built" min={1900} max={CURRENT_YEAR} step={1} fmt={(v) => `${v}`} draft={draft} set={set} />
              </div>

              <div className="ssc-group">
                <span className="ssc-group-title">HOA fee (monthly)</span>
                <RangeSlider loKey="min_hoa" hiKey="max_hoa" min={0} max={2000} step={25} fmt={money} draft={draft} set={set} />
              </div>
              <div className="ssc-group">
                <span className="ssc-group-title">Property tax (annual)</span>
                <RangeSlider loKey="min_tax" hiKey="max_tax" min={0} max={30000} step={500} fmt={money} draft={draft} set={set} />
              </div>

              <div className="ssc-group ssc-grid2">
                <div>
                  <span className="ssc-group-title">Garage</span>
                  <Segmented options={GARAGE_COUNTS} ariaLabel="Minimum garage spaces" value={draft.garage_spaces} onSelect={(v) => set("garage_spaces", v)} />
                </div>
                <div>
                  <span className="ssc-group-title">Listed</span>
                  <select className="ssc-field" aria-label="new_to_market" value={draft.new_to_market ?? 0} onChange={num("new_to_market")}>
                    {NEW_TO_MARKET.map(([v, l]) => <option key={`n${v}`} value={v}>{l}</option>)}
                  </select>
                </div>
              </div>

              <div className="ssc-group">
                <span className="ssc-group-title">Status</span>
                <div className="ssc-pills">
                  {STATUSES.concat(RECENCY).map(([key, label]) => (
                    <Pill key={key} active={!!draft[key]} onClick={toggleBool(key)}>{label}</Pill>
                  ))}
                </div>
              </div>

              <div className="ssc-group">
                <span className="ssc-group-title">Highlights</span>
                <div className="ssc-pills">
                  {FEATURES.map(([key, label]) => (
                    <Pill key={key} active={draft[key] === 1} onClick={toggleFeature(key)}>{label}</Pill>
                  ))}
                </div>
              </div>

              <div className="ssc-group">
                <span className="ssc-group-title">Keywords</span>
                <input type="text" className="ssc-field" aria-label="Keywords" placeholder="e.g. updated kitchen, cul-de-sac"
                       value={draft.keywords ?? ""} onChange={(e) => set("keywords", e.target.value)} />
              </div>
            </div>

            <div className="ssc-modal-foot">
              <button type="button" className="ssc-btn" onClick={clearAll}>Clear all</button>
              <button type="button" className="ssc-btn ssc-btn--primary" onClick={apply}>
                Show results
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
